/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import React from "react";
import { FormattedMessage } from "react-intl";
import { getPageOverlayApis } from "selectors/overlayDropinSelectors";
import { SecurityUtils } from "dc-preview-utils/src/Utils/SecurityUtils";
import { SUBMIT_ACTION_DIALOG_STRINGS as dialogStrings, ACROFORM_CLIENT_NAME } from "constants/constants";
import Worker from "./submitFormAction.worker.js";
import { dialogApi, dialogTypes } from "components/dialogs";
import CommonDialog from "dc-preview-utils/src/components/dialog/CommonDialog";
import loadTranslation from "utils/TranslationUtil";
import localizeComponent from "dc-preview-utils/src/Utils/LocalizationUtil";
import {
  trackSubmitFormActionRequested,
  trackSubmitFormActionTriggered,
  trackSubmitFormActionResponse
} from "analytics/events";
import { getIntl } from "selectors/appSelectors";
import { getUseViewSDKForAcroformSubmit } from "selectors/appSelectors";
import { triggerSubmitFormEvent } from "publicApis/Events/events";
import * as stores from "stores";
import { crossBrowserURLParse } from "dc-preview-utils/src/Utils/UrlUtils";
import { triggerPDFAction } from "pdfActionManager/listenToPDFAction";
import * as BrowserDialog from "components/dialogs/BrowserDialog";

let submitFormWorker;

const showDialog = (title, text, triggeredFrom) => {
  if (triggeredFrom === "jsAction") {
    const message = getIntl(stores).formatMessage({ id: text });
    BrowserDialog.windowAlert({ message });
  } else {
    const confirmLabel = "common.ok";
    dialogApi.show(localizeComponent(CommonDialog, loadTranslation), {
      message: <FormattedMessage id={text} />,
      title: <FormattedMessage id={title} />,
      onConfirm: () => dialogApi.hide(),
      confirmLabel: <FormattedMessage id={confirmLabel} />
    });
  }
};

function responseHandler(response, triggeredFrom) {
  if (response.data.success) {
    showDialog(dialogStrings.SUCCESS_TITLE, dialogStrings.SUCCESS_TEXT, triggeredFrom);
  } else {
    showDialog(dialogStrings.ERROR_TITLE, dialogStrings.FAILURE_TEXT, triggeredFrom);
  }
  if (submitFormWorker) {
    submitFormWorker.terminate();
  }
  trackSubmitFormActionResponse(response.data, triggeredFrom);
}

const createWorkerAndSendFormData = (serverURL, formData, triggeredFrom) => {
  submitFormWorker = new Worker();
  submitFormWorker.onmessage = response => responseHandler(response, triggeredFrom);
  submitFormWorker.postMessage({
    command: "sendFormDataToServer",
    params: {
      ...formData,
      serverURL
    }
  });
};

const onUserConfirmation = (serverURL, formData, triggeredFrom) => {
  if (getUseViewSDKForAcroformSubmit(stores)) {
    // code is running inside a chrome extension
    const event = {
      ...formData,
      triggeredFrom
    };
    triggerSubmitFormEvent(serverURL, event, responseHandler);
  } else {
    createWorkerAndSendFormData(serverURL, formData, triggeredFrom);
  }
  trackSubmitFormActionTriggered(triggeredFrom);
};

const showWarningDialog = (serverURL, formData, triggeredFrom) => {
  const serverUrlOrigin = crossBrowserURLParse(serverURL).origin;
  if (triggeredFrom === "jsAction") {
    const message = getIntl(stores).formatMessage({ id: "dialog.submitAction.warning.text" }, { url: serverUrlOrigin });
    if (BrowserDialog.windowConfirm({ message })) {
      onUserConfirmation(serverURL, formData, triggeredFrom);
    }
  } else {
    dialogApi.show(dialogTypes.WARNING_DIALOG, {
      url: serverUrlOrigin,
      onConfirm: () => {
        dialogApi.hide();
        onUserConfirmation(serverURL, formData, triggeredFrom);
      },
      cancelDialog: () => dialogApi.hide()
    });
  }
};

export async function handleSubmitForm(stores, pdfAction, extraParams = {}) {
  triggerPDFAction(pdfAction);
  const triggeredFrom = extraParams.triggeredFrom || "NonJSAction";
  let formData;
  const serverURL = pdfAction.actionData.URL;
  const protocol = SecurityUtils.getValidURLAndProtocol(serverURL).protocol;
  if (protocol === "https:") {
    const overlayApis = getPageOverlayApis(stores, ACROFORM_CLIENT_NAME);
    if (overlayApis) {
      formData = await overlayApis.getAcroformDataToExport(pdfAction);
    }

    if (formData) {
      if (!formData.isSupportedFormat) {
        showDialog(dialogStrings.ERROR_TITLE, dialogStrings.UNSUPPORTED_FORMAT_TEXT, triggeredFrom);
      } else if (formData.containsRequiredEmptyFields) {
        showDialog(dialogStrings.ERROR_TITLE, dialogStrings.EMPTY_FIELD_TEXT, triggeredFrom);
      } else {
        showWarningDialog(serverURL, formData, triggeredFrom);
      }
    }
  } else {
    showDialog(dialogStrings.ERROR_TITLE, dialogStrings.UNSUPPORTED_PROTOCOL_TEXT, triggeredFrom);
  }
  const { containsRequiredEmptyFields, format } = formData || {};
  trackSubmitFormActionRequested({
    triggeredFrom,
    containsRequiredEmptyFields,
    format,
    protocol
  });
}
